// VIDE CONSTITUICAO_REGRAS.SH sec.2.4 | Art.61.1
// ============================================================================
// RecurrenceExpiryAlert — Alerta visual de recorrências prestes a expirar
// Blueprint P47 Item #21 — Criado em 16/07/2026 (P49)
// ============================================================================
// Lógica:
//   - Agrupa postings ativos por recurrenceGroupId (ou descrição + valor)
//   - Se o grupo tem recorrencia "Mensal" e repeatCount definido,
//     calcula quantas parcelas já existem vs total previsto
//   - Exibe alerta se restam <= 3 parcelas (ou < 20% do total)
//   - Ordena por urgência (menos parcelas restantes primeiro)
// ============================================================================

import React, { useMemo, useState } from "react";

const EXPIRY_THRESHOLD = 3; // restam 3 ou menos parcelas = alerta
const PERCENT_THRESHOLD = 0.2; // menos de 20% restante = alerta

/**
 * @param {Object} props
 * @param {Array} props.postings — Lista de postings carregados
 * @param {Array} props.templates — Lista de templates (assinaturas)
 */
export default function RecurrenceExpiryAlert({ postings, templates }) {
  const [dismissed, setDismissed] = useState(new Set());

  // Agrupa postings recorrentes por recurrenceGroupId
  const expiringGroups = useMemo(() => {
    if (!postings || !postings.length) return [];

    // Filtra só postings ativos e recorrentes
    const recurringPostings = postings.filter(
      (p) =>
        p.status !== "CANCELLED" &&
        p.recorrencia &&
        p.recorrencia.toLowerCase() === "mensal" &&
        p.recurrenceGroupId,
    );

    // Agrupa por recurrenceGroupId
    const groups = {};
    for (const p of recurringPostings) {
      const gid = p.recurrenceGroupId;
      if (!groups[gid]) {
        groups[gid] = {
          groupId: gid,
          description: p.description || "Sem descrição",
          value: p.value,
          category: p.category || "",
          items: [],
          repeatCount: p.repeatCount || 12,
          firstDate: p.date || "",
        };
      }
      groups[gid].items.push(p);
      // Pega a primeira descrição que aparecer (mais legível)
      if (p.description && groups[gid].description === "Sem descrição") {
        groups[gid].description = p.description;
      }
      // Usa o maior repeatCount encontrado
      if ((p.repeatCount || 0) > groups[gid].repeatCount) {
        groups[gid].repeatCount = p.repeatCount;
      }
    }

    // Calcula quantas faltam
    const result = [];
    for (const gid of Object.keys(groups)) {
      if (dismissed.has(gid)) continue;
      const g = groups[gid];
      const existing = g.items.length;
      const total = g.repeatCount;
      const remaining = Math.max(0, total - existing);
      const progress = existing / total;

      // Só alerta se está perto do fim
      if (
        remaining > 0 &&
        (remaining <= EXPIRY_THRESHOLD || progress >= 1 - PERCENT_THRESHOLD)
      ) {
        result.push({
          ...g,
          existing,
          total,
          remaining,
          progress,
        });
      }
    }

    // Ordena por urgência (menos parcelas restantes primeiro)
    result.sort((a, b) => a.remaining - b.remaining);

    return result;
  }, [postings, dismissed]);

  // Também verifica templates com repeatCount (assinaturas futuras)
  const expiringTemplates = useMemo(() => {
    if (!templates || !templates.length) return [];

    return templates
      .filter((t) => {
        if (dismissed.has(`tmpl-${t.id}`)) return false;
        if (!t.recorrencia || t.recorrencia.toLowerCase() !== "mensal")
          return false;
        const total = t.repeatCount || 12;
        const remaining = total; // template ainda não gerou nada
        return remaining <= EXPIRY_THRESHOLD;
      })
      .map((t) => ({
        groupId: `tmpl-${t.id}`,
        description: t.description || "Sem descrição",
        value: t.amount || t.value || 0,
        category: "",
        items: [],
        existing: 0,
        total: t.repeatCount || 12,
        remaining: t.repeatCount || 12,
        progress: 0,
        isTemplate: true,
      }));
  }, [templates, dismissed]);

  const allAlerts = useMemo(() => {
    return [...expiringGroups, ...expiringTemplates];
  }, [expiringGroups, expiringTemplates]);

  if (!allAlerts.length) return null;

  const handleDismiss = (gid) => {
    setDismissed((prev) => new Set([...prev, gid]));
  };

  const handleDismissAll = () => {
    const allIds = allAlerts.map((a) => a.groupId);
    setDismissed((prev) => new Set([...prev, ...allIds]));
  };

  return (
    <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-amber-600 text-sm">⚠️</span>
          <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide">
            Recorrências próximas do fim
          </h3>
          <span className="text-[10px] text-amber-600 font-medium bg-amber-100 px-1.5 py-0.5 rounded-full">
            {allAlerts.length}
          </span>
        </div>
        {allAlerts.length > 1 && (
          <button
            onClick={handleDismissAll}
            className="text-[10px] text-amber-600 hover:text-amber-800 underline"
          >
            Dispensar todos
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {allAlerts.map((alert) => {
          const pct = Math.round(alert.progress * 100);
          const isUrgent = alert.remaining <= 1;
          return (
            <div
              key={alert.groupId}
              className={`flex items-center justify-between p-2 rounded ${
                isUrgent
                  ? "bg-red-50 border border-red-200"
                  : "bg-white border border-amber-200"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-bold uppercase ${
                      isUrgent ? "text-red-600" : "text-amber-700"
                    }`}
                  >
                    {isUrgent ? "🔴 Última" : "🟡 Fim próximo"}
                  </span>
                  <p className="text-[11px] font-semibold text-gray-800 truncate">
                    {alert.description}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] text-gray-500">
                    {alert.existing}/{alert.total} parcelas geradas
                  </span>
                  <span className="text-[10px] text-gray-500">
                    Restam:{" "}
                    <strong
                      className={isUrgent ? "text-red-600" : "text-amber-700"}
                    >
                      {alert.remaining}
                    </strong>
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {pct}% concluído
                  </span>
                </div>
                {/* Barra de progresso */}
                <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isUrgent ? "bg-red-500" : "bg-amber-400"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <button
                onClick={() => handleDismiss(alert.groupId)}
                className="ml-2 text-[10px] text-gray-400 hover:text-gray-600 px-1.5 py-0.5 rounded hover:bg-gray-100"
                title="Dispensar este alerta"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[9px] text-amber-500">
        Crie um novo lançamento ou renove a assinatura para continuar.
      </p>
    </div>
  );
}
